import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, CheckCircle, TrendingUp, ExternalLink, BarChart3, MessageSquare, FileText, Database, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';
import { API_PROD_URL } from 'src/utils/axios';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DotLoader from 'src/components/reusable/dot-loader';

interface CarTReportProps {
  response?: any;
  userId: string | undefined;
  threadId: string | null | undefined;
}

const CarTReport = ({ response, userId, threadId }: CarTReportProps) => {
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  const [selectedSection, setSelectedSection] = useState(0);
  const [showSources, setShowSources] = useState<Record<number, boolean>>({});
  // Pipeline status states
  const [pipelineStatus, setPipelineStatus] = useState<string>('running');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reportData, setReportData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Polling interval in milliseconds (5 seconds)
  const POLL_INTERVAL = 5000;

  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;

    const checkPipelineStatus = async () => {
      try {
        const response = await axios.get(
          `https://report-automation-dev.orangefield-fc298ced.centralus.azurecontainerapps.io/check_pipeline_status`,
          {
            params: {
              userId,
              threadId
            }
          }
        );

        const status = response.data.status || response.data.pipeline_status;
        setPipelineStatus(status);

        if (status === 'complete' || status === 'completed') {
          // Pipeline is complete, fetch the report data
          if (pollInterval) {
            clearInterval(pollInterval);
          }
          await fetchReportData();
        }
      } catch (err) {
        console.error('Error checking pipeline status:', err);
        setError('Failed to check pipeline status');
      }
    };

    const fetchReportData = async () => {
      try {
        const response = await axios.get(
          `${API_PROD_URL}/cart-adoption-intelligence-agent-v2/statistical-analysis`,
          {
            params: {
              userId,
              threadId
            }
          }
        );

        if (response.data && Array.isArray(response.data)) {
          setReportData(response.data);
        } else if (response.data.reportData) {
          setReportData(response.data.reportData);
        } else {
          setReportData([]);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError('Failed to fetch report data');
        setIsLoading(false);
      }
    };

    // Initial check
    checkPipelineStatus();

    // Start polling
    pollInterval = setInterval(checkPipelineStatus, POLL_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [userId, threadId]);

  const toggleSection = (index:number) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleSources = (index:any) => {
    setShowSources(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const ValidationBadge = ({ validation }:any) => {
    const { valid, confidence } = validation;
    const bgColor = valid ? 'bg-green-100' : 'bg-yellow-100';
    const textColor = valid ? 'text-green-800' : 'text-yellow-800';
    const icon = valid ? <CheckCircle className="w-2 h-2" /> : <AlertTriangle className="w-2 h-2" />;
    
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${bgColor} ${textColor} text-xs font-medium`}>
        {icon}
        <span>{valid ? 'Validated' : 'Review Required'}</span>
        <span className="font-bold">{(confidence * 100).toFixed(0)}%</span>
      </div>
    );
  };

  const MetricCard = ({ label, value, color = "blue" }:any) => (
    <div className={`bg-${color}-50 rounded-lg p-2 border border-${color}-200`}>
      <div className={`text-${color}-600 text-sm font-medium mb-1`}>{label}</div>
      <div className={`text-${color}-900 text-2xl font-bold`}>{value}</div>
    </div>
  );

  return (
    <div className="h-[90vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
      {/* Header */}
      <div className=" text-black border-b border-gray-300 ">
        <div className=" px-2 py-3">
          <div className="flex items-center gap-4 ">
            <div className="bg-white/20 p-1 rounded-xl backdrop-blur-sm">
              <BarChart3 className="w-3 h-3" />
            </div>
            <div>
              <h1 className="text-md font-bold">MR Insights Agent Intelligence</h1>
              <p className="text-gray-600 text-xs ">Comprehensive Adoption Analysis & Strategic Insights</p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center px-2">
          {/* <div className="relative">
            <Loader2 className="text-blue-600 animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-10 bg-blue-100 rounded-full animate-pulse"></div>
            </div>
          </div> */}
          <h2 className="mt-6 text-xl font-bold text-gray-800">Generating Market Intelligence Report</h2>
          {/* <p className="mt-2 text-sm text-gray-600 text-center max-w-md">
            Analyzing MR Insights Agent market data, clinical trials, and competitive landscape...
          </p> */}
          <div className="mt-4 flex flex-col items-center gap-2 text-xs text-gray-500">
            <span>Status: {pipelineStatus}</span>
            <DotLoader />
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="px-4 py-3 mx-4 mt-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!isLoading && reportData.length > 0 && (
        <>
          {/* Main Content */}
          <div className=" px-2 pb-3 mt-2">

          {/* Content Area */}
          <div className="space-y-3">
            {reportData.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden transition-all"
              >
                {/* Section Header */}
                <div
                  className="bg-gradient-to-r from-gray-50 to-gray-100 p-2 cursor-pointer hover:from-gray-100 hover:to-gray-200 transition-colors"
                  onClick={() => toggleSection(index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-800 mb-2">
                        {item.section || `Section ${index + 1}`}
                      </h3>
                      {item.subsection && (
                        <p className="text-xs text-gray-600 font-medium mb-2">{item.subsection}</p>
                      )}
                      {item.validation && <ValidationBadge validation={item.validation} />}
                    </div>
                    <button className="ml-4 mt-1 text-gray-500 hover:text-gray-700 transition-colors">
                      {expandedSections[index] ? (
                        <ChevronDown className="w-2 h-2" />
                      ) : (
                        <ChevronRight className="w-2 h-2" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expandable Content */}
                {expandedSections[index] && (
                  <div className="p-3 space-y-3">
                    {/* Dynamic JSON Rendering */}
                    {Object.entries(item).map(([key, value]) => {
                      // Skip rendering these keys as they're already shown or handled separately
                      if (key === 'section' || key === 'validation') return null;
                      
                      // Handle summary/description text fields
                     if (key === "summary" || key === "subsection") {
                        return (
                          <div
                            key={key}
                            className="rounded-2xl border border-gray-200 bg-white shadow-sm p-2 hover:shadow-md transition-all duration-200"
                          >
                            {/* Header */}
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-2 h-2 text-blue-500" />
                              <h4 className="text-base font-semibold text-gray-800 tracking-wide capitalize">
                                {key.replace(/_/g, " ")}
                              </h4>
                            </div>

                            {/* Markdown-rendered summary */}
                            <div className="text-xs prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 hover:prose-a:underline">
                             <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  a: ({ node, ...props }) => (
                                    <a
                                      {...props}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline"
                                    />
                                  ),
                                }}
                              >
                                {String(value)}
                              </ReactMarkdown>
                            </div>
                          </div>
                        );
                      }
                      // Handle arrays (like keyInsights)
                      if (Array.isArray(value)) {
                        return (
                          <div key={key}>
                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                              <TrendingUp className="w-2 h-2" />
                              {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                            </h4>
                            <div className="space-y-2">
                              {/* eslint-disable-next-line react/prop-types */}
                              {value.map((arrayItem: any, idx: number) => {
                                // If array item is an object (like sources)
                                 if (typeof arrayItem === "object" && arrayItem !== null) {
                                    return (
                                      <div
                                        key={idx}
                                        className="group relative flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm transition-all hover:shadow-md hover:border-blue-300"
                                      >
                                        {/* URL */}
                                        <div className="flex items-center justify-between">
                                          <a
                                            href={arrayItem.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm font-semibold text-blue-600 hover:underline break-all"
                                          >
                                            {new URL(arrayItem.url).hostname}
                                          </a>
                                          {/* <ExternalLink
                                            className="h-2 w-2 text-blue-500 opacity-70 group-hover:opacity-100"
                                            strokeWidth={2}
                                          /> */}
                                        </div>

                                        {/* Query */}
                                        <div className="text-xs text-gray-600 italic">
                                          “{arrayItem.query}”
                                        </div>

                                        {/* Reason */}
                                        <div className="text-xs text-gray-800 leading-snug">
                                          <span className="font-medium text-gray-700">Reason:</span>{" "}
                                          {arrayItem.reason}
                                        </div>

                                        {/* Score and Usage */}
                                        <div className="flex justify-between items-center pt-2 border-t border-gray-100 mt-1">
                                          <div className="text-xs text-gray-500">
                                            Score:{" "}
                                            <span className="font-semibold text-gray-800">
                                              {arrayItem.score.toFixed(3)}
                                            </span>
                                          </div>

                                          {arrayItem.used ? (
                                            <div className="flex items-center text-green-600 text-xs font-semibold">
                                              <CheckCircle2 className="h-2 w-2 mr-1" />
                                              Used
                                            </div>
                                          ) : (
                                            <div className="flex items-center text-red-500 text-xs font-semibold">
                                              <XCircle className="h-2 w-2 mr-1" />
                                              Not Used
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                // If array item is a string (like insights)
                                return (
                                  <div
                                    key={idx}
                                    className="flex items-center gap-3 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-500"
                                  >
                                    <CheckCircle className="w-2 h-2 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-700 text-xs leading-relaxed">
                                      {String(arrayItem)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      
                      // Handle objects (nested data)
                     if (typeof value === "object" && value !== null) {
                          const formattedKey =
                            key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
                           // eslint-disable-next-line react/prop-types
                          const { valid, comment, confidence, error_margin, weighted_score }:any = value;

                          return (
                            <div
                              key={key}
                              className="rounded-2xl border border-gray-200 bg-white shadow-sm p-2 hover:shadow-md transition-all duration-200"
                            >
                              {/* Header */}
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                                  {formattedKey}
                                </h4>

                                {valid ? (
                                  <div className="flex items-center text-green-600 text-xs font-semibold">
                                    <CheckCircle2 className="h-2 w-2 mr-1" />
                                    Valid
                                  </div>
                                ) : (
                                  <div className="flex items-center text-red-500 text-xs font-semibold">
                                    <XCircle className="h-2 w-2 mr-1" />
                                    Invalid
                                  </div>
                                )}
                              </div>

                              {/* Comment */}
                              {comment && (
                                <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded-lg border border-gray-100 mb-3">
                                  <AlertTriangle className="inline h-2 w-2 text-yellow-500 mr-1" />
                                  {comment}
                                </div>
                              )}

                              {/* Metrics */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                                  <span className="block text-xs text-gray-500">Confidence</span>
                                  <span className="font-semibold text-gray-800">
                                    {(confidence * 100).toFixed(0)}%
                                  </span>
                                  <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                                    <div
                                      className={`h-1.5 rounded-full ${
                                        confidence > 0.7 ? "bg-green-500" : "bg-yellow-500"
                                      }`}
                                      style={{ width: `${confidence * 100}%` }}
                                    />
                                  </div>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                                  <span className="block text-xs text-gray-500">Error Margin</span>
                                  <span className="font-semibold text-gray-800">
                                    {(error_margin * 100).toFixed(1)}%
                                  </span>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                                  <span className="block text-xs text-gray-500">Weighted Score</span>
                                  <span className="font-semibold text-gray-800">
                                    {weighted_score}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      
                      // Handle primitive values (strings, numbers, booleans)
                      return (
                        <div key={key} className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                          <span className="font-semibold text-gray-600 text-sm">
                            {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}:
                          </span>{' '}
                          <span className="text-gray-700 text-sm">{String(value)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
         
        </div>

        {/* Footer */}
        <div className="mt-3 bg-white rounded-xl shadow-lg p-3">
          <div className="text-center max-w-md mx-auto">
            <h3 className="text-md font-bold text-gray-800 mb-3">Report Methodology</h3>
            <p className="text-gray-600 leading-relaxed mb-4 text-xs">
              This comprehensive market intelligence report aggregates data from multiple authoritative sources including 
              market research firms, clinical trial databases, regulatory filings, and industry publications. Each finding 
              undergoes rigorous statistical validation using cross-referencing algorithms and confidence scoring to ensure 
              data accuracy and reliability.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
              <span className="flex items-center text-xs gap-1">
                <Database className="w-3 h-3" />
                Multi-Source Verification
              </span>
              <span className="flex items-center text-xs gap-1">
                <CheckCircle className="w-3 h-3" />
                Statistical Validation
              </span>
              <span className="flex items-center text-xs gap-1">
                <TrendingUp className="w-3 h-3" />
                Real-Time Updates
              </span>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default CarTReport;